import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { ChevronRight } from "lucide-react";
import { currency } from "../../lib/formatting";
import { propertyMetrics, isSMSF, portfolioFundingSummary, equityReleaseRunwaySimple, equityReleaseRunwayDynamic, annualPaygVariationAmount, computeBucketAllocations } from "../../lib/calculations";
import { getEntityColor } from "../../lib/entityColors";
import { InfoTooltip } from "../ui/InfoTooltip";
import { NumberInput } from "../ui/NumberInput";
import type { PropertyRecord, EquityRelease } from "../../types/portfolio";
import type { Structure, InvestorProfile } from "../../types/entities";
import type { BudgetSettings } from "../../types/budget";
import type { AppSettings } from "../settings/SettingsPage";
import AddFundingSourceModal from "./AddFundingSourceModal";

interface PortfolioFlowProps {
  structures: Structure[];
  properties: PropertyRecord[];
  budgetSettings: BudgetSettings;
  appSettings: AppSettings;
  profile: InvestorProfile;
  includeSMSF: boolean;
  onUpdateStructure?: (s: Structure) => void;
  onUpdateAppSettings?: (s: AppSettings) => void;
  onUpdateProperty?: (p: PropertyRecord) => void;
  onNavigateToLoans?: () => void;
  onNavigateToTax?: () => void;
  monthlySurplus: number;
}

interface SourceCard {
  id: string;
  label: string;
  sublabel?: string;
  amount: number;
  isUserAdded?: boolean;
  /** For equity release sources — carry the property + release for inline editing */
  erProperty?: PropertyRecord;
  erRelease?: EquityRelease;
}

interface DestCard {
  id: string;
  structureIndex: number;
  name: string;
  entityType: string;
  structure: Structure;
  cost: number; // positive = needs funding, negative = surplus
  properties: Array<{
    id: string;
    address: string;
    costShare: number;
    lender?: string;
    accountLast4?: string;
  }>;
}

export default function PortfolioFlow({
  structures, properties, budgetSettings, appSettings, profile, includeSMSF, onUpdateStructure, onUpdateAppSettings, onUpdateProperty, onNavigateToLoans, onNavigateToTax, monthlySurplus,
}: PortfolioFlowProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [expandedSourceIds, setExpandedSourceIds] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const committed = useMemo(() => properties.filter((p) => p.status !== "sold" && !p.notes?.includes("[DRAFT SCENARIO]")), [properties]);

  const fs = useMemo(
    () => portfolioFundingSummary(committed, structures, budgetSettings, appSettings, profile, monthlySurplus),
    [committed, structures, budgetSettings, appSettings, profile, monthlySurplus]
  );

  // ── Sources ──
  const sources = useMemo<SourceCard[]>(() => {
    const items: SourceCard[] = [];
    const allocations = computeBucketAllocations(budgetSettings.buckets, monthlySurplus);
    const propAlloc = allocations.find((a) => a.bucket.type === "property");
    if (propAlloc && propAlloc.actual > 0)
      items.push({
        id: "personal",
        label: "Personal Contribution",
        sublabel: propAlloc.fullyFunded
          ? propAlloc.bucket.name
          : `${propAlloc.bucket.name} · ${currency(Math.round(propAlloc.shortfall))} short`,
        amount: propAlloc.actual,
      });

    // Per equity release with drawdown
    for (const p of committed) {
      for (const r of p.equityReleases ?? []) {
        if (!r.drawdownAmount || r.drawdownAmount <= 0) continue;
        const freq = r.drawdownFrequency === "weekly" ? 52 / 12 : r.drawdownFrequency === "monthly" ? 1 : 26 / 12;
        items.push({
          id: `er-${r.id}`,
          label: `Equity release`,
          sublabel: `${p.nickname ?? p.address} ${r.creditLimit ? `${Math.round(r.creditLimit / 1000)}K` : ""}`.trim(),
          amount: r.drawdownAmount * freq,
          erProperty: p,
          erRelease: r,
        });
      }
    }

    if (fs.taxRefundTotal > 0)
      items.push({ id: "payg", label: "PAYG Tax Refund", amount: fs.taxRefundTotal });

    // User-added funding sources from AppSettings
    for (const src of appSettings.fundingSources ?? []) {
      if (src.type === "equity_release") continue;
      if (src.monthlyAmount <= 0) continue;
      items.push({
        id: src.id,
        label: src.label,
        sublabel: src.type === "tax_refund" ? `Annual ${currency(Math.round(src.annualAmount ?? src.monthlyAmount * 12))}`
                : src.type === "cash_buffer" ? `${currency(src.openingBalance ?? 0)} balance`
                : undefined,
        amount: src.monthlyAmount,
        isUserAdded: true,
      });
    }

    return items;
  }, [committed, budgetSettings, fs.taxRefundTotal, appSettings.fundingSources, monthlySurplus]);

  // ── Destinations ──
  const destinations = useMemo<DestCard[]>(() => {
    const filtered = structures.filter((s) => includeSMSF || !isSMSF(s.entityType));
    return filtered.map((s, i) => {
      const sProps = committed.filter((p) => p.structureId === s.id);
      const sCashflow = sProps.reduce((sum, p) => sum + propertyMetrics(p).netCashflowShare, 0);
      const sPersonalTopUp = sProps.reduce((sum, p) => sum + (p.personalTopUp ?? 0) * ((p.ownershipPercent ?? 100) / 100), 0);
      const cost = Math.max(0, -sCashflow) + sPersonalTopUp;
      return {
        id: s.id,
        structureIndex: i,
        name: s.displayName || s.entityType,
        entityType: s.entityType,
        structure: s,
        cost: sCashflow >= 0 ? -sCashflow : cost, // negative = surplus, positive = needs funding
        properties: sProps.map((p) => ({
          id: p.id,
          address: p.nickname ?? p.address ?? "Unknown",
          costShare: propertyMetrics(p).netCashflowShare,
          lender: p.lender ?? undefined,
          accountLast4: p.loanAccountNumber ? p.loanAccountNumber.slice(-4) : undefined,
        })),
      };
    });
  }, [committed, structures, includeSMSF]);

  const poolAmount = fs.totalFunding;

  const paygData = useMemo(() => {
    const payg = annualPaygVariationAmount(committed, structures, profile);
    return { monthlyAmount: payg.effective / 12, isOverride: profile.paygVariationOverride != null };
  }, [committed, structures, profile]);

  // ── SVG drawing ──
  const drawLines = useCallback(() => {
    const grid = gridRef.current;
    const svg = svgRef.current;
    if (!grid || !svg) return;
    // Only draw on desktop
    if (window.innerWidth < 768) { Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove()); return; }
    const gR = grid.getBoundingClientRect();
    svg.setAttribute("viewBox", `0 0 ${gR.width} ${gR.height}`);
    svg.style.width = `${gR.width}px`;
    svg.style.height = `${gR.height}px`;
    let paths = "";
    const pool = grid.querySelector("[data-pool]");
    if (!pool) { Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove()); return; }
    const pR = pool.getBoundingClientRect();
    const poolLeftX = pR.left - gR.left;
    const poolRightX = pR.right - gR.left;
    const poolTop = pR.top - gR.top;
    const poolH = pR.height;

    // Distribute attachment points along pool edges
    const sourceEls = Array.from(grid.querySelectorAll("[data-source]"));
    const destEls = Array.from(grid.querySelectorAll("[data-dest]"));

    // Adaptive pullout: 40% of available gap, clamped 10-40px
    const sourceMaxRight = sourceEls.length > 0
      ? Math.max(...sourceEls.map((el) => el.getBoundingClientRect().right - gR.left))
      : poolLeftX;
    const destMinLeft = destEls.length > 0
      ? Math.min(...destEls.map((el) => el.getBoundingClientRect().left - gR.left))
      : poolRightX;
    const pulloutSource = Math.max(10, Math.min(40, (poolLeftX - sourceMaxRight) * 0.4));
    const pulloutDest = Math.max(10, Math.min(40, (destMinLeft - poolRightX) * 0.4));

    sourceEls.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const x = r.right - gR.left;
      const y = r.top + r.height / 2 - gR.top;
      const attachY = poolTop + (poolH * (i + 1)) / (sourceEls.length + 1);
      const c1x = x + pulloutSource;
      const c2x = poolLeftX - pulloutSource;
      paths += `<path marker-end="url(#arrow-portfolio)" d="M ${x} ${y} C ${c1x} ${y}, ${c2x} ${attachY}, ${poolLeftX} ${attachY}" />`;
    });

    destEls.forEach((el, i) => {
      const isSurplus = el.getAttribute("data-surplus") === "true";
      const r = el.getBoundingClientRect();
      const x = r.left - gR.left;
      const y = r.top + r.height / 2 - gR.top;
      const attachY = poolTop + (poolH * (i + 1)) / (destEls.length + 1);
      const c1x = poolRightX + pulloutDest;
      const c2x = x - pulloutDest;
      const cls = isSurplus ? ' class="surplus"' : "";
      const markerUrl = isSurplus ? "url(#arrow-portfolio-surplus)" : "url(#arrow-portfolio)";
      paths += `<path${cls} marker-end="${markerUrl}" d="M ${poolRightX} ${attachY} C ${c1x} ${attachY}, ${c2x} ${y}, ${x} ${y}" />`;
    });

    Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove());
    if (paths) {
      const tmp = new DOMParser().parseFromString(`<svg xmlns="http://www.w3.org/2000/svg">${paths}</svg>`, "image/svg+xml");
      Array.from(tmp.documentElement.children).forEach(child => svg.appendChild(child));
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(drawLines, 80);
    const handleResize = () => { clearTimeout(timer); setTimeout(drawLines, 80); };
    window.addEventListener("resize", handleResize);
    return () => { clearTimeout(timer); window.removeEventListener("resize", handleResize); };
  }, [drawLines, expandedIds, sources.length, destinations.length]);

  const toggleEntity = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setTimeout(drawLines, 80);
  };

  const toggleSource = (id: string) => {
    setExpandedSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setTimeout(drawLines, 80);
  };

  // ── Source inline expand content ──
  function renderSourceExpand(s: SourceCard) {
    if (s.erProperty && s.erRelease) {
      const release = s.erRelease;
      const property = s.erProperty;
      const destStructure = structures.find((st) => st.id === release.destinationStructureId);
      const destLabel = destStructure?.displayName || destStructure?.trustName || destStructure?.companyName || "—";
      const remaining = (release.creditLimit ?? 0) - (release.balance ?? 0);
      const simple = equityReleaseRunwaySimple(release);
      const dynamic = equityReleaseRunwayDynamic(release, 0, appSettings.rentGrowthAssumption ?? 0.03);
      const hasDrawdown = (release.drawdownAmount ?? 0) > 0 && (release.creditLimit ?? 0) > 0;
      const isAmber = simple !== null && simple < 12;
      const isRed = simple !== null && simple < 6;

      return (
        <div className="mt-2 space-y-3 px-1">
          {/* Header: source → dest + arrangement badge */}
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-[10px] text-neutral-400">
              {property.nickname ?? property.address} → {destLabel}
              <InfoTooltip tip="An equity release is a credit facility secured against one property, drawn down periodically to fund investments in another entity. Interest deductibility depends on use of funds, not the security property (s.8-1 ITAA 1997)." />
            </span>
            <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${release.arrangement === "informal" ? "bg-amber-100 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
              {release.arrangement === "informal" ? "Informal" : "Formal loan"}
              <InfoTooltip tip="Formal loan: inter-entity loan with a deed — interest is assessable income to the lender and deductible to the borrower. Informal: funds advanced without a deed — interest deductibility still depends on use of funds but no interest income flows back." />
            </span>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Credit Limit</label>
              <div className="mt-0.5 flex items-center gap-1">
                <span className="text-[10px] text-neutral-400">$</span>
                <NumberInput className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  value={release.creditLimit || ""} placeholder="e.g. 250000"
                  onChange={(e) => {
                    if (!onUpdateProperty) return;
                    const val = parseFloat(e.target.value) || 0;
                    const updated = { ...property, equityReleases: (property.equityReleases ?? []).map((r) => r.id === release.id ? { ...r, creditLimit: val, drawdownFrequency: r.drawdownFrequency ?? "fortnightly" } : r) };
                    onUpdateProperty(updated);
                  }}
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Drawdown</label>
              <div className="mt-0.5 flex items-center gap-1">
                <span className="text-[10px] text-neutral-400">$</span>
                <NumberInput className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  value={release.drawdownAmount || ""} placeholder="e.g. 290"
                  onChange={(e) => {
                    if (!onUpdateProperty) return;
                    const val = parseFloat(e.target.value) || 0;
                    const updated = { ...property, equityReleases: (property.equityReleases ?? []).map((r) => r.id === release.id ? { ...r, drawdownAmount: val, drawdownFrequency: r.drawdownFrequency ?? "fortnightly" } : r) };
                    onUpdateProperty(updated);
                  }}
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Frequency</label>
              <select className="mt-0.5 w-full rounded-lg border border-neutral-200 px-2 py-1 text-xs focus:outline-none"
                value={release.drawdownFrequency ?? "fortnightly"}
                onChange={(e) => {
                  if (!onUpdateProperty) return;
                  const updated = { ...property, equityReleases: (property.equityReleases ?? []).map((r) => r.id === release.id ? { ...r, drawdownFrequency: e.target.value as EquityRelease["drawdownFrequency"] } : r) };
                  onUpdateProperty(updated);
                }}
              >
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Current Balance</label>
              <div className="mt-0.5 flex items-center gap-1">
                <span className="text-[10px] text-neutral-400">$</span>
                <NumberInput className="w-full rounded-lg border border-neutral-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  value={release.balance || ""} placeholder="0"
                  onChange={(e) => {
                    if (!onUpdateProperty) return;
                    const val = parseFloat(e.target.value) || 0;
                    const updated = { ...property, equityReleases: (property.equityReleases ?? []).map((r) => r.id === release.id ? { ...r, balance: val } : r) };
                    onUpdateProperty(updated);
                  }}
                />
              </div>
              {(release.creditLimit ?? 0) > 0 && (
                <div className="mt-0.5 text-[10px] text-neutral-400">{currency(Math.max(0, remaining))} remaining</div>
              )}
            </div>
          </div>

          {/* Runway */}
          {hasDrawdown && simple !== null && (
            <div className={`rounded-lg border px-3 py-2 text-[10px] ${isRed ? "bg-red-100 text-red-700 border-red-200" : isAmber ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
              <div className="flex items-center gap-3">
                <div><span className="font-medium">Runway: <InfoTooltip tip="How many months of funding this facility can provide at the current drawdown rate before the credit limit is reached. Simple = flat rate. Dynamic = with rental growth assumption reducing the drawdown requirement over time." /></span> {Math.floor(simple / 12) > 0 ? `${Math.floor(simple / 12)}yr ` : ""}{Math.round(simple % 12)}mo</div>
                {dynamic !== null && <div><span className="font-medium">With {Math.round((appSettings.rentGrowthAssumption ?? 0.03) * 100)}% growth:</span> {Math.floor(dynamic / 12) > 0 ? `${Math.floor(dynamic / 12)}yr ` : ""}{Math.round(dynamic % 12)}mo</div>}
              </div>
              {isAmber && <div className="mt-1 font-medium">{isRed ? "Facility nearly exhausted" : "Running low — review funding strategy"}</div>}
            </div>
          )}

          {/* Deep link */}
          {onNavigateToLoans && (
            <div className="text-right">
              <button onClick={onNavigateToLoans} className="text-[10px] text-neutral-400 hover:text-neutral-600">View loan details on Loans →</button>
            </div>
          )}
        </div>
      );
    }

    if (s.id === "payg") {
      return (
        <div className="mt-2 space-y-1.5 px-1">
          <div className="text-xs text-neutral-600">{currency(Math.round(paygData.monthlyAmount))}/month from approved PAYG variation</div>
          <div className="flex items-center gap-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${paygData.isOverride ? "bg-amber-100 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
              {paygData.isOverride ? "Manual override" : "Auto-derived"}
            </span>
            <InfoTooltip tip={paygData.isOverride ? "You've entered a specific monthly amount on the Tax page. Use this if your accountant has given you a figure that differs from the auto-derived estimate." : "Calculated from your portfolio's estimated annual rental losses × marginal tax rate."} />
          </div>
          {onNavigateToTax && (
            <div className="text-right">
              <button onClick={onNavigateToTax} className="text-[10px] text-neutral-400 hover:text-neutral-600">Configure on Tax page →</button>
            </div>
          )}
        </div>
      );
    }

    if (s.id === "personal") {
      return (
        <div className="mt-2 px-1">
          <div className="text-[10px] text-neutral-400">Set on Buckets page (Property Portfolio bucket).</div>
        </div>
      );
    }

    return null;
  }

  const isExpandableSource = (s: SourceCard) => s.id.startsWith("er-") || s.id === "payg" || s.id === "personal";

  // ── No entities fallback ──
  if (destinations.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-neutral-200 px-6 py-8 text-center text-sm text-neutral-500">
        No portfolio entities yet — add a structure to see funding flow.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Flow diagram ── */}
      <div className="relative" ref={gridRef}>
        {/* SVG overlay (desktop only) */}
        <svg
          ref={svgRef}
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{ overflow: "visible" }}
        >
          <defs>
            <marker id="arrow-portfolio" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse" markerUnits="userSpaceOnUse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#d6d3d1" />
            </marker>
            <marker id="arrow-portfolio-surplus" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse" markerUnits="userSpaceOnUse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#10b981" />
            </marker>
          </defs>
          <style>{`
            svg path { fill: none; stroke: #d6d3d1; stroke-width: 1.5; }
            svg path.surplus { stroke: #10b981; }
          `}</style>
        </svg>

        {/* Desktop: 3-column grid */}
        <div className="hidden md:grid md:grid-cols-[1fr_180px_1fr] gap-x-12 gap-y-4 items-stretch">
          {/* LEFT — Sources + Buffer */}
          <div className="flex flex-col">
            <div className="space-y-3 flex-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Sources (in)</div>
              {sources.map((s) => {
                const expandable = isExpandableSource(s);
                const isSourceExpanded = expandedSourceIds.has(s.id);
                return (
                  <div key={s.id}>
                    <div
                      data-source
                      className={`group relative rounded-xl border border-neutral-200 bg-white px-4 py-3 ${expandable ? "cursor-pointer hover:shadow-sm transition-shadow" : ""}`}
                      onClick={expandable ? () => toggleSource(s.id) : undefined}
                    >
                      <div className="flex items-center gap-1.5">
                        {expandable && <ChevronRight className={`h-3 w-3 text-neutral-400 shrink-0 transition-transform ${isSourceExpanded ? "rotate-90" : ""}`} />}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-neutral-600">{s.label}</div>
                          {s.sublabel && <div className="text-[10px] text-neutral-400 truncate">{s.sublabel}</div>}
                        </div>
                      </div>
                      <div className="mt-1 text-sm font-semibold text-neutral-900">{currency(Math.round(s.amount))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
                      {s.isUserAdded && onUpdateAppSettings && (
                        <button
                          onClick={(e) => { e.stopPropagation(); if (confirm(`Remove "${s.label}" from funding sources?`)) onUpdateAppSettings({ ...appSettings, fundingSources: (appSettings.fundingSources ?? []).filter((f) => f.id !== s.id) }); }}
                          className="absolute top-2 right-2 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full text-neutral-300 hover:bg-red-50 hover:text-red-500 text-sm"
                        >×</button>
                      )}
                    </div>
                    {isSourceExpanded && renderSourceExpand(s)}
                  </div>
                );
              })}
              {sources.length === 0 && (
                <div className="rounded-xl border border-dashed border-neutral-200 px-4 py-3 text-xs text-neutral-400 text-center">No sources configured</div>
              )}
              <button
                onClick={() => setShowAddModal(true)}
                className="w-full rounded-xl border-2 border-dashed border-neutral-200 px-4 py-2.5 text-xs text-neutral-400 hover:border-neutral-400 hover:text-neutral-600 transition-colors"
              >+ Add funding source</button>
            </div>
            {/* Buffer card */}
            <div className="mt-auto pt-4">
              <div className="rounded-xl bg-neutral-50 px-4 py-3">
                <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                  Buffer <InfoTooltip tip="A safety margin added to your total portfolio cost. Accounts for unexpected expenses — vacancies, maintenance, rate rises. Recommended: 10% for established portfolios, higher if loans are approaching fixed-rate expiry or properties are newer." />
                </div>
                <input
                  type="range" min={0} max={20} step={1}
                  value={appSettings.portfolioBufferPercent ?? 10}
                  onChange={(e) => { if (onUpdateAppSettings) onUpdateAppSettings({ ...appSettings, portfolioBufferPercent: parseInt(e.target.value) }); }}
                  className="w-full accent-neutral-900 mt-1"
                />
                <div className="text-sm font-semibold text-neutral-900 mt-0.5">{appSettings.portfolioBufferPercent ?? 10}% · {currency(Math.round(fs.bufferAmount))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
              </div>
            </div>
          </div>

          {/* CENTER — Pool + Gap */}
          <div className="flex flex-col items-center justify-center">
            <div className="flex-1 flex items-center justify-center w-full">
              <div data-pool className="rounded-2xl bg-neutral-900 text-white px-5 py-6 text-center w-full">
                <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 flex items-center justify-center gap-1">
                  Total Funding
                  <InfoTooltip tip="Sum of all configured funding sources: personal contribution, equity releases, PAYG variation, and entity redistribution." />
                </div>
                <div className="mt-1 text-xl font-bold">{currency(Math.round(poolAmount))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
              </div>
            </div>
            {/* Gap card */}
            <div className="mt-auto pt-4 w-full">
              <div className="rounded-xl bg-neutral-50 px-4 py-3 text-center">
                <div className="flex items-center justify-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                  Gap <InfoTooltip tip="Funding minus cost with buffer. Positive = surplus, negative = shortfall." />
                </div>
                <div className={`text-lg font-semibold ${fs.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {fs.gap > 0 ? `-${currency(Math.round(fs.gap))}` : `+${currency(Math.round(Math.abs(fs.gap)))}`}
                  <span className="text-xs font-normal text-neutral-400">/mo</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Destinations + Portfolio Cost */}
          <div className="flex flex-col">
            <div className="space-y-2 flex-1">
              <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400">Destinations (out)</div>
              {destinations.map((d) => {
              const color = getEntityColor(d.structureIndex);
              const isExpanded = expandedIds.has(d.id);
              const isSurplus = d.cost <= 0;
              return (
                <div key={d.id}>
                  <div
                    data-dest
                    data-surplus={isSurplus}
                    className={`rounded-xl border bg-white overflow-hidden cursor-pointer hover:shadow-sm transition-shadow ${isSurplus ? "border-emerald-200 bg-emerald-50/30" : "border-neutral-200"}`}
                    style={{ borderRightWidth: 4, borderRightColor: color }}
                    onClick={() => d.properties.length > 0 && toggleEntity(d.id)}
                  >
                    <div className="flex items-center gap-2 px-4 py-3">
                      {d.properties.length > 0 && (
                        <ChevronRight className={`h-3.5 w-3.5 text-neutral-400 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      )}
                      <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
                      <span className="flex-1 text-sm font-medium text-neutral-900 truncate">{d.name}</span>
                      <span className={`text-sm font-semibold shrink-0 ${isSurplus ? "text-emerald-600" : "text-red-600"}`}>
                        {isSurplus ? `+${currency(Math.round(Math.abs(d.cost)))}` : currency(Math.round(d.cost))}/mo
                      </span>
                    </div>
                  </div>
                  {/* Drill-down */}
                  {isExpanded && d.properties.length > 0 && (
                    <div className="ml-6 mt-1 space-y-1 mb-2">
                      {/* Redistribution toggle — surplus entities only */}
                      {isSurplus && onUpdateStructure && (
                        <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2.5">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">Surplus handling</span>
                            <InfoTooltip tip="How this entity's surplus cashflow is handled each month. Hold = leave in entity account. Redistribute = flow back to personal funding pool. Pay down debt = extra repayments on this entity's loans." />
                          </div>
                          <div className="flex rounded-md border border-neutral-200 bg-white overflow-hidden text-[11px]">
                            {([{ value: "hold", label: "Hold" }, { value: "redistribute", label: "Redistribute" }, { value: "pay_down_debt", label: "Pay down debt" }] as const).map((opt) => (
                              <button key={opt.value} onClick={(e) => { e.stopPropagation(); onUpdateStructure({ ...d.structure, redistributionMode: opt.value }); }}
                                className={`flex-1 px-2.5 py-1 transition-colors ${(d.structure.redistributionMode ?? "hold") === opt.value ? "bg-neutral-900 text-white" : "text-neutral-500 hover:bg-neutral-50"}`}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {d.properties.map((p) => (
                        <div key={p.id} className="rounded-lg border-l-2 bg-white px-3 py-2" style={{ borderLeftColor: color }}>
                          <div className="text-xs font-medium text-neutral-800 truncate">{p.address}</div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] text-neutral-400">
                              {p.lender ? `${p.lender}${p.accountLast4 ? ` · ****${p.accountLast4}` : ""}` : "No loan account configured"}
                            </span>
                            <span className={`text-xs font-medium ${p.costShare >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {p.costShare >= 0 ? `+${currency(Math.round(p.costShare))}` : currency(Math.round(Math.abs(p.costShare)))}/mo
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            {/* Portfolio Cost card */}
            <div className="mt-auto pt-4">
              <div className="rounded-xl bg-neutral-50 px-4 py-3">
                <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                  Portfolio cost <InfoTooltip tip="Total monthly cost across all entities that need funding (negative cashflow entities)." />
                </div>
                <div className="text-lg font-semibold text-neutral-900">{currency(Math.round(fs.totalCost))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: stacked vertical */}
        <div className="md:hidden space-y-4">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 mb-2">Sources (in)</div>
            <div className="space-y-2">
              {sources.map((s) => {
                const expandable = isExpandableSource(s);
                const isSourceExpanded = expandedSourceIds.has(s.id);
                return (
                  <div key={s.id}>
                    <div
                      className={`rounded-xl border border-neutral-200 bg-white px-4 py-3 flex items-center justify-between ${expandable ? "cursor-pointer" : ""}`}
                      onClick={expandable ? () => toggleSource(s.id) : undefined}
                    >
                      <div className="flex items-center gap-1.5">
                        {expandable && <ChevronRight className={`h-3 w-3 text-neutral-400 shrink-0 transition-transform ${isSourceExpanded ? "rotate-90" : ""}`} />}
                        <div>
                          <div className="text-xs font-medium text-neutral-600">{s.label}</div>
                          {s.sublabel && <div className="text-[10px] text-neutral-400">{s.sublabel}</div>}
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-neutral-900">{currency(Math.round(s.amount))}/mo</div>
                    </div>
                    {isSourceExpanded && renderSourceExpand(s)}
                  </div>
                );
              })}
              {sources.length === 0 && <div className="text-xs text-neutral-400">No sources configured</div>}
            </div>
          </div>

          <div className="flex justify-center">
            <div className="rounded-2xl bg-neutral-900 text-white px-6 py-4 text-center">
              <div className="text-[10px] uppercase tracking-wide text-neutral-400">Total Funding</div>
              <div className="text-xl font-bold">{currency(Math.round(poolAmount))}/mo</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-400 mb-2">Destinations (out)</div>
            <div className="space-y-2">
              {destinations.map((d) => {
                const color = getEntityColor(d.structureIndex);
                const isExpanded = expandedIds.has(d.id);
                const isSurplus = d.cost <= 0;
                return (
                  <div key={d.id}>
                    <div
                      className={`rounded-xl border bg-white overflow-hidden ${isSurplus ? "border-emerald-200" : "border-neutral-200"}`}
                      style={{ borderLeftWidth: 4, borderLeftColor: color }}
                      onClick={() => d.properties.length > 0 && toggleEntity(d.id)}
                    >
                      <div className="flex items-center gap-2 px-4 py-3">
                        {d.properties.length > 0 && <ChevronRight className={`h-3.5 w-3.5 text-neutral-400 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`} />}
                        <span className="flex-1 text-sm font-medium text-neutral-900 truncate">{d.name}</span>
                        <span className={`text-sm font-semibold ${isSurplus ? "text-emerald-600" : "text-red-600"}`}>
                          {isSurplus ? `+${currency(Math.round(Math.abs(d.cost)))}` : currency(Math.round(d.cost))}/mo
                        </span>
                      </div>
                    </div>
                    {isExpanded && d.properties.length > 0 && (
                      <div className="ml-4 mt-1 space-y-1 mb-2">
                        {isSurplus && onUpdateStructure && (
                          <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2.5">
                            <div className="flex items-center gap-1.5 mb-2">
                              <span className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">Surplus handling</span>
                            </div>
                            <div className="flex rounded-md border border-neutral-200 bg-white overflow-hidden text-[11px]">
                              {([{ value: "hold", label: "Hold" }, { value: "redistribute", label: "Redistribute" }, { value: "pay_down_debt", label: "Pay down debt" }] as const).map((opt) => (
                                <button key={opt.value} onClick={(e) => { e.stopPropagation(); onUpdateStructure({ ...d.structure, redistributionMode: opt.value }); }}
                                  className={`flex-1 px-2.5 py-1 transition-colors ${(d.structure.redistributionMode ?? "hold") === opt.value ? "bg-neutral-900 text-white" : "text-neutral-500 hover:bg-neutral-50"}`}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {d.properties.map((p) => (
                          <div key={p.id} className="rounded-lg border-l-2 bg-white px-3 py-2" style={{ borderLeftColor: color }}>
                            <div className="text-xs font-medium text-neutral-800 truncate">{p.address}</div>
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-[10px] text-neutral-400">
                                {p.lender ? `${p.lender}${p.accountLast4 ? ` · ****${p.accountLast4}` : ""}` : "No loan account"}
                              </span>
                              <span className={`text-xs font-medium ${p.costShare >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                {p.costShare >= 0 ? `+${currency(Math.round(p.costShare))}` : currency(Math.round(Math.abs(p.costShare)))}/mo
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile summary cards (hidden on desktop — embedded in columns above) */}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        <div className="rounded-xl bg-neutral-50 px-4 py-3">
          <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
            Buffer <InfoTooltip tip="A safety margin added to your total portfolio cost. Accounts for unexpected expenses — vacancies, maintenance, rate rises. Recommended: 10% for established portfolios, higher if loans are approaching fixed-rate expiry or properties are newer." />
          </div>
          <input
            type="range" min={0} max={20} step={1}
            value={appSettings.portfolioBufferPercent ?? 10}
            onChange={(e) => { if (onUpdateAppSettings) onUpdateAppSettings({ ...appSettings, portfolioBufferPercent: parseInt(e.target.value) }); }}
            className="w-full accent-neutral-900 mt-1"
          />
          <div className="text-sm font-semibold text-neutral-900 mt-0.5">{appSettings.portfolioBufferPercent ?? 10}% · {currency(Math.round(fs.bufferAmount))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
        </div>
        <div className="rounded-xl bg-neutral-50 px-4 py-3">
          <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
            Gap <InfoTooltip tip="Funding minus cost with buffer. Positive = surplus, negative = shortfall." />
          </div>
          <div className={`text-lg font-semibold ${fs.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>
            {fs.gap > 0 ? `-${currency(Math.round(fs.gap))}` : `+${currency(Math.round(Math.abs(fs.gap)))}`}
            <span className="text-xs font-normal text-neutral-400">/mo</span>
          </div>
        </div>
        <div className="rounded-xl bg-neutral-50 px-4 py-3">
          <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
            Portfolio cost <InfoTooltip tip="Total monthly cost across all entities that need funding (negative cashflow entities)." />
          </div>
          <div className="text-lg font-semibold text-neutral-900">{currency(Math.round(fs.totalCost))}<span className="text-xs font-normal text-neutral-400">/mo</span></div>
        </div>
      </div>

      {/* Add funding source modal */}
      {showAddModal && onUpdateAppSettings && (
        <AddFundingSourceModal
          onAdd={(source) => {
            onUpdateAppSettings({ ...appSettings, fundingSources: [...(appSettings.fundingSources ?? []), source] });
            setShowAddModal(false);
          }}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}
